
import React, { useState, useEffect } from 'react';
import { Task, TaskStatus, User, AppNotification, Country } from '../types';
import TaskBoard from '../components/TaskBoard';
import TaskList from '../components/TaskList';
import { TaskFormModal } from '../components/TaskFormModal';
import { apiService } from '../services/apiService';
import { Plus, LayoutGrid, List as ListIcon, Loader2, Search, Users, ShieldCheck } from 'lucide-react';

interface TaskModuleProps {
  currentUser: User;
  onAddNotification?: (notif: AppNotification) => void;
  activeCountry: Country;
}

const TaskModule: React.FC<TaskModuleProps> = ({ currentUser, onAddNotification, activeCountry }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [staffRegistry, setStaffRegistry] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<'board' | 'list'>('board');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const isAdmin = currentUser.role === 'Admin';
  const canAddEdit = isAdmin || currentUser.role === 'Add/Edit';
  const isReadOnly = currentUser.role === 'Read Only';

  useEffect(() => {
    syncModuleData();
  }, [activeCountry]);

  const syncModuleData = async () => {
    setIsLoading(true);
    try {
        const [fetchedUsers, fetchedTasks] = await Promise.all([
            apiService.getUsers(),
            apiService.getTasks(activeCountry)
        ]);
        setStaffRegistry(fetchedUsers.filter(u => u.status === 'active'));
        setTasks(fetchedTasks);
    } catch (e) {
        console.error("Task Module Sync Error:", e);
    } finally {
        setIsLoading(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: TaskStatus) => {
    if (isReadOnly) return;
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const updatedTask = { ...task, status: newStatus };
    await apiService.saveTask(updatedTask);
    setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t));
    
    if (onAddNotification && newStatus === 'Completed') {
        onAddNotification({
            id: crypto.randomUUID(),
            type: 'success',
            title: 'Task Finalized',
            message: `"${task.title}" was marked as completed.`,
            date: new Date().toISOString(),
            read: false
        });
    }
  };

  const handleSaveTask = async (taskData: Partial<Task>) => {
    if (!canAddEdit) return;
    const taskToSave: Task = editingTask 
      ? { ...editingTask, ...taskData } as Task
      : {
          id: `TSK-${Date.now()}`,
          title: taskData.title || 'Untitled Task',
          description: taskData.description || '',
          priority: taskData.priority || 'Medium',
          dueDate: taskData.dueDate || new Date().toISOString().split('T')[0],
          assigneeId: taskData.assigneeId,
          status: 'Pending',
          country: activeCountry
        };
    
    await apiService.saveTask(taskToSave);
    await syncModuleData();
    setShowFormModal(false);
  };

  const filteredTasks = tasks.filter(t => 
    t.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-indigo-600 mb-4" />
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Syncing Operational Ledger...</p>
      </div>
    );
  }

  return (
    <div className="p-8 h-full flex flex-col space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-800 tracking-tight uppercase">Operational Board</h2>
          <p className="text-slate-500 font-medium">Branch deliverables and resource allocation.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="bg-white p-1 rounded-2xl border border-slate-200 shadow-sm flex">
            <button onClick={() => setActiveTab('board')} className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === 'board' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-indigo-600'}`}>Kanban</button>
            <button onClick={() => setActiveTab('list')} className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === 'list' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-indigo-600'}`}>Registry</button>
          </div>
          {canAddEdit && (
            <button 
                onClick={() => { setEditingTask(null); setShowFormModal(true); }} 
                className="bg-indigo-600 text-white px-6 py-2.5 rounded-2xl font-bold shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-2 active:scale-95"
            >
                <Plus size={18} /> New Task
            </button>
          )}
        </div>
      </header>

      <div className="flex-1 flex flex-col min-h-0 space-y-6">
        <div className="relative max-w-md w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input 
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl text-sm outline-none focus:ring-4 focus:ring-indigo-500/10 shadow-sm transition-all"
          />
        </div>

        <div className="flex-1 overflow-hidden">
            {activeTab === 'board' ? (
                <TaskBoard tasks={filteredTasks} users={staffRegistry} onUpdateStatus={handleUpdateTaskStatus} onEditTask={(t) => { if (canAddEdit) { setEditingTask(t); setShowFormModal(true); } }} />
            ) : (
                <TaskList tasks={filteredTasks} contacts={[]} users={staffRegistry} onToggleTask={(id) => { if (isReadOnly) return; const t = tasks.find(it => it.id === id); if (t) handleUpdateTaskStatus(id, t.status === 'Completed' ? 'Pending' : 'Completed'); }} onDeleteTask={(id) => isAdmin && apiService.deleteTask(id).then(()=>syncModuleData())} onSelectContact={() => {}} />
            )}
        </div>
      </div>

      <TaskFormModal isOpen={showFormModal} onClose={() => setShowFormModal(false)} onSave={handleSaveTask} editingTask={editingTask} users={staffRegistry} tasks={tasks} />
    </div>
  );
};

export default TaskModule;
